import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Divider,
} from "@mui/material";
import {
  MdDelete,
  MdEdit,
  MdAdd,
  MdSearch,
  MdVisibility,
  MdWork,
  MdOutlineDesignServices,
  MdCategory,
  MdAttachMoney,
  MdGridView,
  MdViewList,
  MdRefresh,
  MdClear,
  MdImage,
  MdCheckCircle,
  MdCancel,
  MdStar,
} from "react-icons/md";
import toast from "react-hot-toast";
import { axiosInstance } from "../apis/config";
import { getAllCategories } from "../apis/categoryApi";
import { getAllTypes } from "../apis/typesApi";
import { getAllSubCategories } from "../apis/subCategoryApi";
import { getAllservices, toggleServiceStatus } from "../apis/servicesApi";
import { brand } from "../theme";
import { useAuth } from "../context/AuthProvider";

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Services = () => {
  const { role } = useAuth();
  const isVendor = role === "vendor";

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [subcategoriesAll, setSubcategoriesAll] = useState([]);
  const [types, setTypes] = useState([]);
  const [allTypes, setAllTypes] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [viewService, setViewService] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [current, setCurrent] = useState({
    id: null,
    name: "",
    description: "",
    price: "",
    salePrice: "",
    classicPrice: "",
    standardPrice: "",
    primePrice: "",
    classicDescription: "",
    standardDescription: "",
    primeDescription: "",
    imageFile: null,
    category_id: "",
    subcategory_id: "",
    image: "",
  });

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await getAllservices();
      setServices(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error("Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const [catData, typeData, subData] = await Promise.all([
          getAllCategories().catch(() => []),
          getAllTypes().catch(() => []),
          getAllSubCategories().catch(() => []),
        ]);
        setCategories(Array.isArray(catData) ? catData : []);
        setAllTypes(Array.isArray(typeData) ? typeData : []);
        setSubcategoriesAll(Array.isArray(subData) ? subData : []);
      } catch (err) {
        toast.error("Failed to load catalog categories or types");
      }
      fetchServices();
    };
    init();
  }, []);

  const stats = useMemo(() => {
    let activeCount = 0;
    let multiTierCount = 0;
    const catSet = new Set();

    services.forEach((s) => {
      if (s.is_active === 1 || s.is_active === true) activeCount += 1;
      if (s.classicPrice || s.standardPrice || s.primePrice) multiTierCount += 1;
      if (s.category_id) catSet.add(s.category_id);
    });

    return {
      total: services.length,
      activeCount,
      multiTierCount,
      categoriesCount: catSet.size || categories.length,
    };
  }, [services, categories]);

  const filteredServices = useMemo(() => {
    return services.filter((srv) => {
      if (categoryFilter !== "all" && String(srv.category_id) !== String(categoryFilter)) {
        return false;
      }

      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const name = (srv.name || "").toLowerCase();
      const desc = (srv.description || "").toLowerCase();

      return name.includes(q) || desc.includes(q);
    });
  }, [services, categoryFilter, search]);

  const paginatedServices = useMemo(() => {
    return filteredServices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredServices, page, rowsPerPage]);

  /* ------------------ HANDLERS ------------------ */

  const handleOpen = (service = null) => {
    if (isVendor) return;
    if (service) {
      setEditMode(true);
      const filteredSubs = subcategoriesAll.filter(
        (sub) => Number(sub.category_id) === Number(service.category_id)
      );
      setSubcategories(filteredSubs);
      const filteredTypes = allTypes.filter(
        (type) => Number(type.subcategory_id) === Number(service.subcategory_id)
      );
      setTypes(filteredTypes);

      setCurrent({
        id: service.id,
        name: service.name || "",
        description: service.description || "",
        price: service.price || "",
        salePrice: service.salePrice || "",
        classicPrice: service.classicPrice || "",
        standardPrice: service.standardPrice || "",
        primePrice: service.primePrice || "",
        classicDescription: service.classicDescription || "",
        standardDescription: service.standardDescription || "",
        primeDescription: service.primeDescription || "",
        category_id: service.category_id || "",
        subcategory_id: service.subcategory_id || "",
        imageFile: null,
        image: service.image || "",
      });
    } else {
      setEditMode(false);
      setCurrent({
        id: null,
        name: "",
        description: "",
        price: "",
        salePrice: "",
        classicPrice: "",
        standardPrice: "",
        primePrice: "",
        classicDescription: "",
        standardDescription: "",
        primeDescription: "",
        imageFile: null,
        category_id: "",
        subcategory_id: "",
        image: "",
      });
      setSubcategories([]);
      setTypes([]);
    }

    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setCurrent({
      id: null,
      name: "",
      description: "",
      price: "",
      salePrice: "",
      classicPrice: "",
      standardPrice: "",
      primePrice: "",
      classicDescription: "",
      standardDescription: "",
      primeDescription: "",
      imageFile: null,
      category_id: "",
      subcategory_id: "",
      image: "",
    });
    setSubcategories([]);
    setTypes([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "category_id") {
      const filteredSubs = subcategoriesAll.filter(
        (sub) => Number(sub.category_id) === Number(value)
      );
      setSubcategories(filteredSubs);
      setTypes([]);
      setCurrent((prev) => ({
        ...prev,
        category_id: value,
        subcategory_id: "",
        type_id: "",
      }));
    } else if (name === "subcategory_id") {
      const filteredTypes = allTypes.filter(
        (type) => Number(type.subcategory_id) === Number(value)
      );
      setTypes(filteredTypes);
      setCurrent((prev) => ({
        ...prev,
        subcategory_id: value,
        type_id: "",
      }));
    } else {
      setCurrent((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCurrent((prev) => ({ ...prev, imageFile: e.target.files[0] }));
    }
  };

  const handleSave = async () => {
    if (isVendor) {
      toast.error("Vendors are not permitted to create or modify services");
      return;
    }
    if (!current.name || !current.price || !current.category_id || !current.subcategory_id) {
      toast.error("Service Name, Price, Category & Subcategory are required");
      return;
    }

    setSaving(true);
    const formData = new FormData();
    formData.append("name", current.name);
    formData.append("description", current.description);
    formData.append("price", current.price);
    formData.append("salePrice", current.salePrice);
    if (current.classicPrice) formData.append("classicPrice", current.classicPrice);
    if (current.standardPrice) formData.append("standardPrice", current.standardPrice);
    if (current.primePrice) formData.append("primePrice", current.primePrice);
    if (current.classicDescription) formData.append("classicDescription", current.classicDescription);
    if (current.standardDescription) formData.append("standardDescription", current.standardDescription);
    if (current.primeDescription) formData.append("primeDescription", current.primeDescription);
    formData.append("category_id", current.category_id);
    formData.append("subcategory_id", current.subcategory_id);

    if (current.type_id) formData.append("type_id", current.type_id);
    if (current.imageFile) formData.append("image", current.imageFile);

    try {
      if (editMode) {
        await axiosInstance.post(`/services/${current.id}`, formData);
        toast.success("Service updated successfully");
      } else {
        await axiosInstance.post("/services", formData);
        toast.success("Service created successfully");
      }
      handleClose();
      fetchServices();
    } catch (err) {
      toast.error("Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (isVendor) {
      toast.error("Vendors are not permitted to delete services");
      return;
    }
    try {
      await axiosInstance.delete(`/services/${deleteId}`);
      toast.success("Service deleted successfully");
      fetchServices();
    } catch (err) {
      toast.error("Failed to delete service");
    } finally {
      setOpenDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const handleToggleStatus = async (srvId) => {
    if (isVendor) {
      toast.error("Vendors are not permitted to change service status");
      return;
    }
    try {
      await toggleServiceStatus(srvId);
      toast.success("Service active status updated");
      fetchServices();
    } catch {
      toast.error("Failed to update active status");
    }
  };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Services Catalog
            </Typography>
            <Chip
              label={`${services.length} Active Services`}
              size="small"
              sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Manage service offerings, pricing tiers (Classic, Standard, Premium), categories, and visibility
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload service catalog">
            <IconButton
              onClick={fetchServices}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          {!isVendor && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleOpen()}
              startIcon={<MdAdd size={20} />}
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: "14px",
                fontWeight: 700,
                fontSize: "0.88rem",
                boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
              }}
            >
              Add New Service
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdOutlineDesignServices size={24} />} label="Total Offerings" count={stats.total} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdCheckCircle size={24} />} label="Active Catalog Items" count={stats.activeCount} accent="#10B981" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdStar size={24} />}
            label="Multi-Tier Services"
            count={stats.multiTierCount}
            accent={brand.brass}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdCategory size={24} />}
            label="Categories Covered"
            count={stats.categoriesCount}
            accent={brand.ink950}
          />
        </Grid>
      </Grid>

      {/* ── Filter Controls Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Bar */}
          <TextField
            placeholder="Search service by name or description..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", md: 340 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Category Filters & View Toggle */}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label={`All (${services.length})`}
                onClick={() => {
                  setCategoryFilter("all");
                  setPage(0);
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 0.5,
                  bgcolor: categoryFilter === "all" ? brand.ink950 : `${brand.slateLight}25`,
                  color: categoryFilter === "all" ? brand.parchment : brand.slate,
                  "&:hover": { bgcolor: categoryFilter === "all" ? brand.ink800 : `${brand.slateLight}40` },
                }}
              />
              {categories.map((cat) => (
                <Chip
                  key={cat.id}
                  label={cat.name}
                  onClick={() => {
                    setCategoryFilter(String(cat.id));
                    setPage(0);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    px: 0.5,
                    bgcolor: String(categoryFilter) === String(cat.id) ? brand.brass : `${brand.slateLight}25`,
                    color: String(categoryFilter) === String(cat.id) ? brand.ink950 : brand.slate,
                    "&:hover": { bgcolor: String(categoryFilter) === String(cat.id) ? brand.brassLight : `${brand.slateLight}40` },
                  }}
                />
              ))}
            </Box>

            {/* View Switcher */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                bgcolor: `${brand.slateLight}20`,
                p: 0.4,
                borderRadius: "12px",
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "9px",
                  px: 1.5,
                  py: 0.5,
                  color: brand.slate,
                  "&.Mui-selected": {
                    bgcolor: brand.paper,
                    color: brand.ink950,
                    boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <MdViewList size={18} />
              </ToggleButton>
              <ToggleButton value="grid">
                <MdGridView size={18} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {/* ── Table vs Grid View ─────────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Thumbnail</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Service Name</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Base Price (₹)</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Active Toggle</TableCell>
                <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedServices.length > 0 ? (
                paginatedServices.map((srv, index) => (
                  <TableRow
                    key={srv.id || index}
                    sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}
                  >
                    <TableCell
                      sx={{
                        color: brand.slate,
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontSize: "0.82rem",
                        fontWeight: 600,
                      }}
                    >
                      {String(page * rowsPerPage + index + 1).padStart(2, "0")}
                    </TableCell>
                    <TableCell>
                      {srv.image ? (
                        <Box
                          component="img"
                          src={`http://skishore.in/api/public/${srv.image}`}
                          alt={srv.name}
                          sx={{
                            width: 52,
                            height: 40,
                            borderRadius: "10px",
                            objectFit: "cover",
                            border: `1px solid ${brand.slateLight}50`,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 52,
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: `${brand.slateLight}30`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: brand.slate,
                          }}
                        >
                          <MdImage size={20} />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.92rem", color: brand.ink950 }}>
                      {srv.name}
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.82rem", maxWidth: 220 }} noWrap>
                      {srv.description || "No description provided"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: brand.ink950, fontSize: "0.92rem" }}>
                      ₹{Number(srv.price || 0).toLocaleString("en-IN")}
                      {srv.salePrice && (
                        <Typography component="span" sx={{ fontSize: "0.72rem", color: brand.danger, ml: 1, textDecoration: "line-through" }}>
                          ₹{srv.salePrice}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={srv.is_active === 1 || srv.is_active === true}
                        onChange={() => handleToggleStatus(srv.id)}
                        disabled={isVendor}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0.8} justifyContent="flex-end">
                        <MuiTooltip title="Inspect Tiers & Details">
                          <IconButton
                            size="small"
                            onClick={() => setViewService(srv)}
                            sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                          >
                            <MdVisibility size={16} />
                          </IconButton>
                        </MuiTooltip>
                        {!isVendor && (
                          <>
                            <MuiTooltip title="Edit Service Catalog">
                              <IconButton
                                size="small"
                                onClick={() => handleOpen(srv)}
                                sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                              >
                                <MdEdit size={16} />
                              </IconButton>
                            </MuiTooltip>
                            <MuiTooltip title="Delete Service">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDeleteId(srv.id);
                                  setOpenDeleteDialog(true);
                                }}
                                sx={{ bgcolor: `${brand.danger}15`, color: brand.danger }}
                              >
                                <MdDelete size={16} />
                              </IconButton>
                            </MuiTooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <MdOutlineDesignServices size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No service catalog items found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {search || categoryFilter !== "all"
                        ? "Try modifying your search query or category filter."
                        : "Click 'Add New Service' to create your first offering."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredServices.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedServices.map((srv) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={srv.id}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "22px",
                  bgcolor: brand.paper,
                  border: `1px solid ${brand.slateLight}40`,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                    borderColor: `${brand.brass}66`,
                  },
                }}
              >
                {/* Service Header Image */}
                <Box position="relative" height={140} bgcolor={`${brand.ink950}10`}>
                  {srv.image ? (
                    <Box
                      component="img"
                      src={`http://skishore.in/api/public/${srv.image}`}
                      alt={srv.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%" color={brand.slate}>
                      <MdImage size={36} />
                    </Box>
                  )}
                  <Box position="absolute" top={10} right={10}>
                    <Switch
                      checked={srv.is_active === 1 || srv.is_active === true}
                      onChange={() => handleToggleStatus(srv.id)}
                      color="success"
                      size="small"
                      sx={{ bgcolor: "rgba(255,255,255,0.8)", borderRadius: "14px" }}
                    />
                  </Box>
                </Box>

                <Box p={2.5}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: brand.ink950 }} noWrap>
                    {srv.name}
                  </Typography>

                  <Typography sx={{ fontSize: "0.8rem", color: brand.slate, mt: 0.5, height: 38, overflow: "hidden" }}>
                    {srv.description || "No description available"}
                  </Typography>

                  <Box display="flex" alignItems="baseline" justifyContent="space-between" mt={2} pt={1.5} borderTop={`1px solid ${brand.slateLight}30`}>
                    <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 600 }}>Base Price</Typography>
                    <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.3rem", color: brand.ink950 }}>
                      ₹{Number(srv.price || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" gap={1} justifyContent="flex-end" p={2} pt={0}>
                  <Button size="small" variant="outlined" onClick={() => setViewService(srv)} startIcon={<MdVisibility />}>
                    Details
                  </Button>
                  {!isVendor && (
                    <Button size="small" variant="outlined" onClick={() => handleOpen(srv)} startIcon={<MdEdit />}>
                      Edit
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Dialog: Service Details & Tiers Inspection ─────────────────── */}
      <Dialog
        open={!!viewService}
        onClose={() => setViewService(null)}
        PaperProps={{ sx: { borderRadius: "24px", p: 1, width: "100%", maxWidth: 540 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.4rem" }}>
          Service Offering Details
        </DialogTitle>
        <DialogContent dividers>
          {viewService && (
            <Box display="flex" flexDirection="column" gap={2} my={1}>
              {viewService.image && (
                <Box
                  component="img"
                  src={`http://skishore.in/api/public/${viewService.image}`}
                  alt={viewService.name}
                  sx={{ width: "100%", height: 180, borderRadius: "16px", objectFit: "cover" }}
                />
              )}

              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: brand.ink950 }}>
                  {viewService.name}
                </Typography>
                <Typography sx={{ fontSize: "0.88rem", color: brand.slate, mt: 0.5 }}>
                  {viewService.description || "No general description specified."}
                </Typography>
              </Box>

              <Divider />

              {/* Pricing Overview */}
              <Box bgcolor={`${brand.brassSoft}77`} p={2} borderRadius="14px">
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>Regular Price</Typography>
                    <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.3rem", color: brand.ink950 }}>
                      ₹{viewService.price}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>Sale Price</Typography>
                    <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.3rem", color: brand.danger }}>
                      {viewService.salePrice ? `₹${viewService.salePrice}` : "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Tier Breakdown */}
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: brand.ink950, mt: 1 }}>
                Configured Service Tiers
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  { name: "Classic Tier", price: viewService.classicPrice, desc: viewService.classicDescription },
                  { name: "Standard Tier", price: viewService.standardPrice, desc: viewService.standardDescription },
                  { name: "Premium Tier", price: viewService.primePrice, desc: viewService.primeDescription },
                ].map((tier) => (
                  <Grid item xs={12} key={tier.name}>
                    <Paper
                      elevation={0}
                      sx={{ p: 1.5, borderRadius: "12px", bgcolor: `${brand.slateLight}15`, border: `1px solid ${brand.slateLight}30` }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: brand.ink950 }}>
                          {tier.name}
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: brand.brass }}>
                          {tier.price ? `₹${tier.price}` : "Not Configured"}
                        </Typography>
                      </Box>
                      {tier.desc && (
                        <Typography sx={{ fontSize: "0.78rem", color: brand.slate, mt: 0.5 }}>
                          {tier.desc}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewService(null)} variant="contained" color="secondary" sx={{ fontWeight: 700, borderRadius: "10px" }}>
            Close Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Add / Edit Service ─────────────────────────────────── */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "22px" } }}>
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          {editMode ? "Edit Service Offering" : "Add New Service Offering"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Service Name *"
                name="name"
                value={current.name}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                label="Regular Base Price (₹) *"
                name="price"
                value={current.price}
                onChange={handleChange}
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                label="Sale Price (₹)"
                name="salePrice"
                value={current.salePrice || ""}
                onChange={handleChange}
                fullWidth
                type="number"
              />
            </Grid>

            {/* Category Cascade */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Category *"
                name="category_id"
                value={current.category_id}
                onChange={handleChange}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Subcategory *"
                name="subcategory_id"
                value={current.subcategory_id}
                onChange={handleChange}
                fullWidth
                SelectProps={{ native: true }}
                disabled={!current.category_id}
              >
                <option value="">Select Subcategory</option>
                {subcategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="General Overview & Description"
                name="description"
                value={current.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            {/* Pricing Tiers Section */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: brand.ink950, mb: 1 }}>
                Multi-Tier Pricing & Descriptions
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Classic Price (₹)"
                name="classicPrice"
                value={current.classicPrice || ""}
                onChange={handleChange}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Standard Price (₹)"
                name="standardPrice"
                value={current.standardPrice || ""}
                onChange={handleChange}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Premium Price (₹)"
                name="primePrice"
                value={current.primePrice || ""}
                onChange={handleChange}
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Classic Description"
                name="classicDescription"
                value={current.classicDescription}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Standard Description"
                name="standardDescription"
                value={current.standardDescription}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Premium Description"
                name="primeDescription"
                value={current.primeDescription}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            {/* Image Upload Preview */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: brand.slate, mb: 1 }}>
                Service Cover Image
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                {(current.image || current.imageFile) && (
                  <Box
                    component="img"
                    src={
                      current.imageFile
                        ? URL.createObjectURL(current.imageFile)
                        : `http://skishore.in/api/public/${current.image}`
                    }
                    alt="Preview"
                    sx={{ width: 100, height: 70, borderRadius: "10px", objectFit: "cover" }}
                  />
                )}
                <Button variant="outlined" component="label" sx={{ borderRadius: "10px", textTransform: "none" }}>
                  Upload Cover Image
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="secondary" disabled={saving} sx={{ fontWeight: 700, borderRadius: "10px", px: 3 }}>
            {saving ? "Saving..." : editMode ? "Update Service" : "Add Service"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Delete ─────────────────────────────────────── */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: "20px", p: 1 } }}>
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.9rem" }}>
            Are you sure you want to delete this service offering? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={{ fontWeight: 700 }}>
            Delete Service
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;